/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Stack, Select, Group, Alert } from "@mantine/core";
import { useTRPC } from "../../../../trpc";
import { FormattedMessage, useIntl } from "react-intl";
import { AsideModal } from "../../../../components/AsideModal";
import { MapLibreMap } from "@mapka/react-maplibre-map";
import { getSourceAndLayers } from "../utils/example";
import { IconExclamationCircle } from "@tabler/icons-react";
import { useDefaultApiKey } from "../../../../hooks/apiKey";
import { useQuery } from "@tanstack/react-query";
import { useNavigateMap } from "../../map/hooks/navigate";
import { useSetExtraSpec } from "../../../../hooks/style";
import type { Tileset, TilesetDataset, TilesetVersion } from "../types/tileset";
import type { AccountInfo } from "../../../../types/account";
import type { LayerSpecification, StyleSpecification } from "@maplibre/maplibre-gl-style-spec";
import type { StyleWithVersion } from "../../map/types/map";
import { useMapkaNotification } from "../../../../hooks/notifications.js";

interface AddTilesetToMapModalProps {
  open: boolean;
  onClose: () => void;
  account: AccountInfo;
  tileset: Tileset;
  version: TilesetVersion;
  dataset: TilesetDataset | null;
}

export const AddTilesetToMapModal = ({ open, onClose, account, tileset, version, dataset }: AddTilesetToMapModalProps) => {
  const navigate = useNavigateMap();
  const intl = useIntl();
  const apiKey = useDefaultApiKey();
  const extraSourcesAndLayers = useSetExtraSpec();
  const trpc = useTRPC();
  const mapkaNotification = useMapkaNotification();

  const [selected, setSelected] = useState<string | null>(null);
  const [privateStyles, setPrivateStyles] = useState<StyleWithVersion[]>([]);

  const { data: styles = [] } = useQuery(trpc.getStylesWithCurrentVersion.queryOptions());

  useEffect(() => {
    const privates = styles.filter(s => s.style.scope === "private");
    const [
      firstPrivateStyle,
    ] = privates;
    if (firstPrivateStyle) {
      setSelected(firstPrivateStyle.style.key);
      setPrivateStyles(privates as unknown as StyleWithVersion[]);
    }
  }, [styles]);

  const selectedStyle = useMemo(() => {
    return privateStyles.find(({ style }) => style.key === selected);
  }, [privateStyles, selected]);

  const sourceAndLayers = useMemo(() => {
    return getSourceAndLayers(
      account,
      tileset,
      version,
      dataset,
    );
  }, [tileset, dataset, version]);

  const sourceAlreadyExists = useMemo(() => {
    if (!selectedStyle) return false;

    const {
      sources: sourcesFromTileset,
    } = sourceAndLayers;

    const {
      sources: sourcesFromSelectedStyle,
    } = selectedStyle.version.spec;

    return !(new Set(Object.keys(sourcesFromTileset)).isDisjointFrom(new Set(Object.keys(sourcesFromSelectedStyle))));
  }, [selectedStyle, sourceAndLayers]);

  const specWithTileset = useMemo(() => {
    if (!selectedStyle) return;

    const {
      version: {
        spec,
      },
    } = selectedStyle;

    const {
      sources: tilesetSources,
      layers: tilesetLayers,
    } = sourceAndLayers;

    return sourceAlreadyExists
      ? spec as unknown as StyleSpecification
      : {
          ...spec,
          sources: {
            ...spec.sources,
            ...tilesetSources,
          },
          layers: [
            ...spec.layers as unknown as LayerSpecification[],
            ...tilesetLayers,
          ],
        } as StyleSpecification;
  }, [selectedStyle, sourceAndLayers, sourceAlreadyExists]);

  const handleSubmit = useCallback(() => {
    if (!selectedStyle || !specWithTileset) return;

    extraSourcesAndLayers(sourceAndLayers);
    navigate(selectedStyle.account.name, selectedStyle.style.key);
    mapkaNotification({
      title: intl.formatMessage({
        defaultMessage: "Added tileset",
        id: "notification.successAddTilesetToMapTitle",
      }),
      message: intl.formatMessage({
        defaultMessage: "Save map to persist changes",
        id: "notification.successAddTilesetToMapMessage",
      }),
      variant: "success",
    });
    onClose();
  }, [navigate, onClose, selectedStyle, sourceAndLayers, specWithTileset]);

  const selectMapLabel = intl.formatMessage({
    defaultMessage: "Select a map",
    id: "label.selectMap",
  });

  const hasPrivateStyles = privateStyles.length > 0;

  return (
    <AsideModal
      opened={open}
      onClose={onClose}
      asideVisibleFrom="xs"
      title={intl.formatMessage({
        defaultMessage: "Add tileset `{key}` to a map",
        id: "label.addToMapTitle",
      }, { key: tileset.key })}
      size="78rem"
      aside={(
        <MapLibreMap
          apiKey={apiKey}
          height="900px"
          width="100%"
          style={specWithTileset}
        />
      )}
    >
      <Stack>
        {hasPrivateStyles && (
          <>
            <Select
              label={selectMapLabel}
              data={privateStyles.map(s => ({
                value: s.style.key,
                label: s.style.displayName,
              }))}
              value={selected}
              placeholder={selectMapLabel}
              onChange={setSelected}
              searchable
            />
            {sourceAlreadyExists && (
              <>
                <Alert variant="light" color="yellow" icon={<IconExclamationCircle />}>
                  <FormattedMessage
                    id="text.sourceAlreadyExists"
                    defaultMessage="Source already exists on map"
                  />
                </Alert>

                <Group gap="xs" justify="flex-end">
                  <Button variant="secondary" type="button" onClick={onClose}>
                    <FormattedMessage defaultMessage="Ok" id="button.ok" />
                  </Button>
                </Group>
              </>
            )}
            {!sourceAlreadyExists && (
              <Group gap="xs" justify="flex-end">
                <Button variant="secondary" type="button" onClick={onClose}>
                  <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
                </Button>

                <Button variant="tertiary" type="button" disabled={!selectedStyle} onClick={handleSubmit}>
                  <FormattedMessage defaultMessage="Add to map" id="button.addToMap" />
                </Button>
              </Group>
            )}
          </>
        )}
        {!hasPrivateStyles && (
          <>
            <Alert variant="light" color="yellow" icon={<IconExclamationCircle />}>
              <FormattedMessage
                id="text.noPrivateMaps"
                defaultMessage="You don't have any private maps. Create a new map to add a tileset."
              />
            </Alert>

            <Group gap="xs" justify="flex-end" pt="lg">
              <Button variant="secondary" type="button" onClick={onClose}>
                <FormattedMessage defaultMessage="Ok" id="button.ok" />
              </Button>
            </Group>
          </>
        )}

      </Stack>
    </AsideModal>
  );
};
